import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import UpgradeGate from '../components/UpgradeGate';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Box, Monitor, Armchair, Bed, Scissors, Link } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESOURCE_TYPES = [
  { value: 'room', label: 'Room', icon: Box, desc: 'Treatment room, wax room, lash room' },
  { value: 'station', label: 'Station', icon: Monitor, desc: 'Nail station, styling chair' },
  { value: 'chair', label: 'Chair', icon: Armchair, desc: 'Barber chair, pedicure chair' },
  { value: 'bed', label: 'Bed', icon: Bed, desc: 'Massage bed, facial bed' },
  { value: 'equipment', label: 'Equipment', icon: Scissors, desc: 'Shared equipment, laser, machine' },
];

export default function ResourcesPage() {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [services, setServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'room', quantity: 1, description: '', linked_services: [] });
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [resRes, svcRes] = await Promise.all([
        axios.get(`${API}/resources`, { withCredentials: true }),
        axios.get(`${API}/services`, { withCredentials: true }),
      ]);
      setResources(resRes.data);
      setServices(svcRes.data);
      setGated(false);
    } catch (err) {
      if (err.response?.status === 403) setGated(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', type: 'room', quantity: 1, description: '', linked_services: [] });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ name: r.name, type: r.type, quantity: r.quantity, description: r.description || '', linked_services: r.linked_services || [] });
    setDialogOpen(true);
  };

  const saveResource = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editId) {
        await axios.put(`${API}/resources/${editId}`, form, { withCredentials: true });
        toast.success('Resource updated');
      } else {
        await axios.post(`${API}/resources`, form, { withCredentials: true });
        toast.success('Resource created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteResource = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await axios.delete(`${API}/resources/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(`${API}/resources/${r.id}`, { is_active: !r.is_active }, { withCredentials: true });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const toggleLinkedService = (svcId) => {
    setForm(p => ({
      ...p,
      linked_services: p.linked_services.includes(svcId)
        ? p.linked_services.filter(s => s !== svcId)
        : [...p.linked_services, svcId]
    }));
  };

  if (loading) return <div className="p-8 text-center text-[#5C6656]">Loading...</div>;
  if (gated) return <UpgradeGate feature="resources" />;

  const TypeIcon = (type) => {
    const found = RESOURCE_TYPES.find(t => t.value === type);
    return found ? found.icon : Box;
  };

  return (
    <div className="fade-in space-y-6" data-testid="resources-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>Resources</h1>
          <p className="text-sm text-[#5C6656] mt-1">Manage rooms, stations, and equipment to prevent double-booking</p>
        </div>
        <Button onClick={openCreate} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white gap-1.5" data-testid="add-resource-btn">
          <Plus size={16} /> Add Resource
        </Button>
      </div>

      <Badge className="bg-[#556B2F]/10 text-[#556B2F] text-xs border-0">Premium Feature</Badge>

      {resources.length === 0 ? (
        <Card className="border-[#E5E7E1]">
          <CardContent className="py-16 text-center">
            <Box size={40} className="mx-auto mb-3 text-[#E5E7E1]" />
            <p className="text-sm font-medium text-[#1A2421]">No resources yet</p>
            <p className="text-xs text-[#5C6656] mt-1">Add rooms, stations, or equipment your services need</p>
            <Button onClick={openCreate} className="mt-4 bg-[#556B2F] text-white" data-testid="add-first-resource">Add your first resource</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {resources.map(r => {
            const Icon = TypeIcon(r.type);
            const linkedCount = (r.linked_services || []).length;
            return (
              <Card key={r.id} className={`border-[#E5E7E1] transition-shadow hover:shadow-md ${!r.is_active ? 'opacity-50' : ''}`} data-testid={`resource-${r.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#E9EDDF] flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-[#556B2F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{r.name}</h3>
                        <p className="text-xs text-[#5C6656] capitalize">{r.type} · Qty: {r.quantity}</p>
                      </div>
                    </div>
                    <Switch checked={r.is_active} onCheckedChange={() => toggleActive(r)} />
                  </div>
                  {r.description && <p className="text-xs text-[#5C6656] mt-3">{r.description}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Link size={12} className="text-[#556B2F]" />
                      <span className="text-xs text-[#5C6656]">{linkedCount} service{linkedCount !== 1 ? 's' : ''} linked</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="h-7 w-7 p-0 text-[#5C6656] hover:text-[#556B2F]" data-testid={`edit-resource-${r.id}`}><Pencil size={13} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteResource(r.id)} className="h-7 w-7 p-0 text-[#E2725B]" data-testid={`delete-resource-${r.id}`}><Trash2 size={13} /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg border-[#E5E7E1] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{editId ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#5C6656] uppercase">Resource name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Nail Station 1" className="border-[#E5E7E1]" data-testid="resource-name" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold text-[#5C6656] uppercase">Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {RESOURCE_TYPES.map(rt => {
                  const Icon = rt.icon;
                  return (
                    <button key={rt.value} type="button" onClick={() => setForm(p => ({ ...p, type: rt.value }))}
                      className={`p-3 rounded-xl border text-left transition-colors ${form.type === rt.value ? 'border-[#556B2F] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#556B2F]/50'}`}
                      data-testid={`type-${rt.value}`}>
                      <Icon size={18} className={form.type === rt.value ? 'text-[#556B2F]' : 'text-[#5C6656]'} />
                      <p className="text-sm font-medium text-[#1A2421] mt-1">{rt.label}</p>
                      <p className="text-[10px] text-[#5C6656]">{rt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs font-bold text-[#5C6656] uppercase">Quantity</Label>
                <Input type="number" min="1" max="50" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} className="border-[#E5E7E1]" data-testid="resource-quantity" />
                <p className="text-[10px] text-[#5C6656]">How many of this resource you have</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-[#5C6656] uppercase">Description</Label>
                <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Optional notes..." className="border-[#E5E7E1] text-sm" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold text-[#5C6656] uppercase">Linked services</Label>
              <p className="text-[10px] text-[#5C6656]">Services that require this resource when booked</p>
              <div className="max-h-48 overflow-y-auto border border-[#E5E7E1] rounded-lg divide-y divide-[#E5E7E1]">
                {services.map(svc => {
                  const linked = form.linked_services.includes(svc.id);
                  return (
                    <button key={svc.id} type="button" onClick={() => toggleLinkedService(svc.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${linked ? 'bg-[#F5F7F0]' : 'hover:bg-[#FAFBF7]'}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${linked ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#B0B5A8]'}`}>
                        {linked && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#1A2421] truncate">{svc.name}</p>
                        <p className="text-[10px] text-[#5C6656]">{svc.duration}min · {'\u00A3'}{svc.price?.toFixed(2)}</p>
                      </div>
                    </button>
                  );
                })}
                {services.length === 0 && <p className="px-3 py-4 text-center text-xs text-[#5C6656]">No services found</p>}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E7E1]">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E5E7E1]">Cancel</Button>
              <Button onClick={saveResource} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="save-resource-btn">{editId ? 'Save Changes' : 'Create Resource'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
